<script setup lang="ts">
import { ref, watch, onMounted, computed, type ComputedRef } from 'vue';
import { useConfigProfilesStore } from '@/stores/config-profiles';
import { useClientsStore } from '@/stores/clients';
import { useClientGroupsStore } from '@/stores/client-groups';
import { useUserGroupsStore } from '@/stores/user-groups';
import { useUsersStore } from '@/stores/user';
import { useI18n } from 'vue-i18n';
import type { ConfigProfiles } from '@/types/graphql';
import { useToast }  from 'primevue/usetoast';

const props = defineProps<{
  isOpen: boolean;
  groupId?: number | null;
  clientMac?: string | null;
  clientMacs?: string[];
  userGroupId?: number | null;
  userId?: number | null;
}>();

const emit = defineEmits(['update:isOpen', 'profile-assigned']);
const { t } = useI18n();
const configProfileStore = useConfigProfilesStore();
const clientsStore = useClientsStore();
const clientGroupsStore = useClientGroupsStore();
const userGroupsStore = useUserGroupsStore();
const usersStore = useUsersStore();

// dialog + loading
const isDialogOpen = ref(false);
const activeLoadRequests = ref(0);
const isLoadingProfiles = computed(() => activeLoadRequests.value > 0);
const isSaving = ref(false);
const loading = computed(() => isLoadingProfiles.value || isSaving.value);
const toast = useToast();

// data
const profiles = ref<ConfigProfiles[]>([]);
const assignedProfiles = ref<ConfigProfiles[]>([]);
const selectedProfiles = ref<number[]>([]);
const initialAssignedIds = ref<number[]>([]);

// drag state
const draggedProfile = ref<ConfigProfiles | null>(null);
const draggedCombinedIndex = ref<number | null>(null);
const isDropActive = ref(false);

watch(isDialogOpen, (newValue) => {
  emit('update:isOpen', newValue);
});

// Placeholder lookup functions (replace with real store/API calls)
async function getGroupNameById(groupId: number): Promise<string> {
  try {
    // First check if we already have the group in the store
    let group = clientGroupsStore.groups.find((g: any) => g.id === groupId);

    // If not found, try to fetch all groups
    if (!group) {
      await clientGroupsStore.getGroups();
      group = clientGroupsStore.groups.find((g: any) => g.id === groupId);
    }

    // Return the group name if found, otherwise return the ID as string
    return group?.name || String(groupId);
  } catch (error) {
    console.error('Error fetching group name:', error);
    return String(groupId);
  }
}

async function getUserGroupNameById(userGroupId: number): Promise<string> {
  try {
    // First check if we already have the user group in the store
    let userGroup = userGroupsStore.userGroups.find((g: any) => g.id === userGroupId);

    // If not found, try to fetch all user groups
    if (!userGroup) {
      await userGroupsStore.getAllUserGroups();
      userGroup = userGroupsStore.userGroups.find((g: any) => g.id === userGroupId);
    }

    // Return the user group name if found, otherwise return the ID as string
    return userGroup?.name || String(userGroupId);
  } catch (error) {
    console.error('Error fetching user group name:', error);
    return String(userGroupId);
  }
}
async function getUserNameById(userId: number): Promise<string> {
  try {
    // First check if we already have the user in the store
    let user = usersStore.users.find((u: any) => u.id === userId);

    // If not found, try to fetch all users
    if (!user) {
      await usersStore.getAllUsers();
      user = usersStore.users.find((u: any) => u.id === userId);
    }

    // Return the user full name if found, otherwise return the ID as string
    return user?.fullName || String(userId);
  } catch (error) {
    console.error('Error fetching user name:', error);
    return String(userId);
  }
}

// Utility: get clientId from clientMac using clientsStore
async function getClientIdByMac(clientMac: string): Promise<number | null> {
  if (!clientMac) {
    return null;
  }
  let clients = clientsStore.clients;
  if (!clients || clients.length === 0) {
    if (typeof clientsStore.getClients === 'function') {
      await clientsStore.getClients();
      clients = clientsStore.clients;
    }
  }
  if (!clients || clients.length === 0) {
    return null;
  }
  const found = clients.find((c: any) => {
    const macNorm = (c.networkInterfaces && c.networkInterfaces[0]?.macAddress)
      ? c.networkInterfaces[0].macAddress.replace(/:/g, '-')
      : '';
    return macNorm === clientMac;
  });
  if (!found) {
    return null;
  }
  if (typeof found.id !== 'number') {
    return null;
  }
  return found.id;
}

// load all profiles, excluding those that are assigned in backend snapshot
async function loadProfiles() {
  activeLoadRequests.value++;
  try {
    let targetType: 'client' | 'clientGroup' | 'user' | 'userGroup' | undefined = undefined;
    let targetKey: string | undefined = undefined;
    if (props.clientMacs && props.clientMacs.length > 0 && typeof props.clientMacs[0] === 'string' && props.clientMacs[0]) {
      targetType = 'client';
      // Set targetKey to MAC address directly for client assignment
      targetKey = props.clientMacs[0];
    } else if (typeof props.clientMac === 'string' && props.clientMac) {
      targetType = 'client';
      // Set targetKey to MAC address directly for client assignment
      targetKey = props.clientMac;
    } else if (props.groupId) {
      targetType = 'clientGroup';
      targetKey = await getGroupNameById(props.groupId);
    } else if (props.userGroupId) {
      targetType = 'userGroup';
      targetKey = await getUserGroupNameById(props.userGroupId);
    } else if (props.userId) {
      targetType = 'user';
      targetKey = await getUserNameById(props.userId);
    }
    // Defensive: ensure targetKey is not empty, undefined, or 'NaN'
    if (targetType && targetKey && targetKey !== '' && targetKey !== 'NaN' && targetKey !== 'undefined') {
      const result = await configProfileStore.getProfilesForAssignmentStore(targetType, targetKey);
      profiles.value = (result || []).map((profile: any) => ({
        id: profile.id,
        profileName: profile.profileName,
        configContent: '',
        description: '',
        __typename: 'ConfigProfiles',
      }));
    } else {
      profiles.value = [];
    }
  } catch (error) {
    console.error('Error loading profiles:', error);
  } finally {
    activeLoadRequests.value = Math.max(0, activeLoadRequests.value - 1);
  }
}

// load assigned profiles for the current target (client/group/user/userGroup)
async function loadAssignedProfiles() {
  activeLoadRequests.value++;
  try {
    if (props.clientMacs && props.clientMacs.length > 0) {
      // For bulk, just load the first client as a representative (or merge logic as needed)
      const clientId = await getClientIdByMac(props.clientMacs[0]);
      let result = null;
      if (clientId !== null) {
        result = await configProfileStore.getClientAssignedProfiles(clientId);
      }
      assignedProfiles.value = (result?.getClientConfigProfiles || []).map((profile: any) => ({
        id: profile.id,
        profileName: profile.profileName,
        configContent: profile.configContent || '',
        description: profile.description || '',
        __typename: 'ConfigProfiles',
      }));
    } else if (props.clientMac) {
      const clientId = await getClientIdByMac(props.clientMac);
      let result = null;
      if (clientId !== null) {
        result = await configProfileStore.getClientAssignedProfiles(clientId);
      }
      assignedProfiles.value = (result?.getClientConfigProfiles || []).map((profile: any) => ({
        id: profile.id,
        profileName: profile.profileName,
        configContent: profile.configContent || '',
        description: profile.description || '',
        __typename: 'ConfigProfiles',
      }));
    } else if (props.groupId) {
      const result = await configProfileStore.getGroupAssignedProfiles(props.groupId);
      assignedProfiles.value = (result?.getGroupConfigProfiles || []).map((profile: any) => ({
        id: profile.id,
        profileName: profile.profileName,
        configContent: profile.configContent || '',
        description: profile.description || '',
        __typename: 'ConfigProfiles',
      }));
    } else if (props.userGroupId) {
      const result = await configProfileStore.getUserGroupAssignedProfiles(props.userGroupId);
      assignedProfiles.value = (result?.getUserGroupConfigProfiles || []).map((profile: any) => ({
        id: profile.id,
        profileName: profile.profileName,
        configContent: profile.configContent || '',
        description: profile.description || '',
        __typename: 'ConfigProfiles',
      }));
    } else if (props.userId) {
      const result = await configProfileStore.getUserAssignedProfiles(props.userId);
      assignedProfiles.value = (result?.getUserConfigProfiles || []).map((profile: any) => ({
        id: profile.id,
        profileName: profile.profileName,
        configContent: profile.configContent || '',
        description: profile.description || '',
        __typename: 'ConfigProfiles',
      }));
    } else {
      assignedProfiles.value = [];
    }

    // snapshot original ids from backend
    initialAssignedIds.value = assignedProfiles.value.map((p: any) => p.id);

    // current selection initially equals original assignment
    selectedProfiles.value = [...initialAssignedIds.value];
  } catch (error) {
    console.error('Error loading assigned profiles:', error);
  } finally {
    activeLoadRequests.value = Math.max(0, activeLoadRequests.value - 1);
  }
}

watch(
  () => props.isOpen,
  async (newValue) => {
    isDialogOpen.value = newValue;
    if (newValue) {
      await loadAssignedProfiles();
      await loadProfiles();
    }
  }
);

// combined list for the right column: current selection in order
const combinedProfiles = computed(() => {
  return selectedProfiles.value
    .map((id) =>
      assignedProfiles.value.find((p: any) => p.id === id) ||
      profiles.value.find((p: any) => p?.id === id)
    )
    .filter((p): p is ConfigProfiles => !!p);
}) as ComputedRef<ConfigProfiles[]>;

// true if anything changed vs original backend state
const hasChanges = computed(() => {
  const prev = initialAssignedIds.value;
  const next = selectedProfiles.value;

  // length changed (including "all removed")
  if (prev.length !== next.length) {
    return true;
  }

  // same length, check ids/order
  return next.some((id, index) => prev[index] !== id);
});

function onCombinedDragStart(event: DragEvent, index: number) {
  draggedCombinedIndex.value = index;
}

function onCombinedDragOver(event: DragEvent, overIndex: number) {
  event.preventDefault();
  if (
    draggedCombinedIndex.value !== null &&
    draggedCombinedIndex.value !== overIndex
  ) {
    const ids = [...selectedProfiles.value];
    const [moved] = ids.splice(draggedCombinedIndex.value, 1);
    ids.splice(overIndex, 0, moved);
    selectedProfiles.value = ids;
    draggedCombinedIndex.value = overIndex;
  }
}

async function handleProfileAssignment() {
  isSaving.value = true;

  const previousIds = [...initialAssignedIds.value];
  const nextIds = [...selectedProfiles.value];

  const prevSet = new Set(previousIds);
  const nextSet = new Set(nextIds);

  const toAdd = Array.from(nextSet).filter((id) => !prevSet.has(id));
  const toRemove = Array.from(prevSet).filter((id) => !nextSet.has(id));

  const orderChanged =
    previousIds.length === nextIds.length &&
    nextIds.some((id, index) => previousIds[index] !== id);

  if (toAdd.length === 0 && toRemove.length === 0 && !orderChanged) {
    closeDialog();
    return;
  }

  async function refreshAffectedClients(clientIds: number[]) {
    if (clientIds.length === 0) return;

    await Promise.allSettled(
      clientIds.map((id) => clientsStore.refreshClientInStore(id))
    );
  }

  async function getGroupClientIds(groupId: number): Promise<number[]> {
    if (!clientsStore.clients || clientsStore.clients.length === 0) {
      await clientsStore.getClients();
    }

    return clientsStore.clients
      .filter((c: any) => c.clientGroupId === groupId)
      .map((c: any) => c.id)
      .filter((id: any): id is number => typeof id === 'number');
  }

  async function getAffectedClientIds(): Promise<number[]> {
    if (props.clientMacs && props.clientMacs.length > 0) {
      const ids = await Promise.all(
        props.clientMacs.map((mac) => getClientIdByMac(mac))
      );

      return ids.filter((id): id is number => id !== null);
    }

    if (props.clientMac) {
      const id = await getClientIdByMac(props.clientMac);
      return id !== null ? [id] : [];
    }

    if (props.groupId) {
      return getGroupClientIds(props.groupId);
    }

    return [];
  }

  function patchAssignedProfileFlag(
    clientIds: number[],
    hasAssignedProfiles: boolean
  ) {
    if (clientIds.length === 0) return;

    for (const clientId of clientIds) {
      const idx = clientsStore.clients.findIndex((c: any) => c.id === clientId);

      if (idx > -1) {
        clientsStore.clients[idx] = {
          ...(clientsStore.clients[idx] as any),
          hasAssignedProfiles,
        } as any;
      }
    }

    clientsStore.clients = [...clientsStore.clients];
  }

  const affectedClientIds = await getAffectedClientIds();

  if (affectedClientIds.length > 0) {
    clientsStore.setProfileAssignmentPending(affectedClientIds, true);
  }

  const runAssignment = async () => {
    try {
      if (toRemove.length > 0) {
        if (props.clientMacs && props.clientMacs.length > 0) {
          for (const mac of props.clientMacs) {
            const clientId = await getClientIdByMac(mac);
            if (clientId !== null) {
              await configProfileStore.removeClientAssignedProfile(
                toRemove,
                clientId
              );
            }
          }
        } else if (props.clientMac) {
          const clientId = await getClientIdByMac(props.clientMac);
          if (clientId !== null) {
            await configProfileStore.removeClientAssignedProfile(
              toRemove,
              clientId
            );
          }
        } else if (props.groupId) {
          await configProfileStore.removeGroupAssignedProfile(
            props.groupId,
            toRemove
          );
        } else if (props.userId) {
          await configProfileStore.removeUserAssignedProfile(
            props.userId,
            toRemove
          );
        } else if (props.userGroupId) {
          await configProfileStore.removeUserGroupAssignedProfile(
            props.userGroupId,
            toRemove
          );
        }
      }

      if (toAdd.length > 0) {
        if (props.clientMacs && props.clientMacs.length > 0) {
          for (const mac of props.clientMacs) {
            const clientId = await getClientIdByMac(mac);
            if (clientId !== null) {
              await configProfileStore.assignProfileToClient(toAdd, clientId);
            }
          }
        } else if (props.clientMac) {
          const clientId = await getClientIdByMac(props.clientMac);
          if (clientId !== null) {
            await configProfileStore.assignProfileToClient(toAdd, clientId);
          }
        } else if (props.groupId) {
          await configProfileStore.assignProfileToGroup(props.groupId, toAdd);
        } else if (props.userId) {
          await configProfileStore.assignProfileToUser(toAdd, props.userId);
        } else if (props.userGroupId) {
          await configProfileStore.assignProfileToUserGroup(
            toAdd,
            props.userGroupId
          );
        }
      }

      if (
        nextIds.length > 0 &&
        (orderChanged || toAdd.length > 0 || toRemove.length > 0)
      ) {
        if (props.clientMacs && props.clientMacs.length > 0) {
          for (const mac of props.clientMacs) {
            const clientId = await getClientIdByMac(mac);
            if (clientId !== null) {
              await configProfileStore.reorderClientProfiles(clientId, nextIds);
            }
          }
        } else if (props.clientMac) {
          const clientId = await getClientIdByMac(props.clientMac);
          if (clientId !== null) {
            await configProfileStore.reorderClientProfiles(clientId, nextIds);
          }
        } else if (props.groupId) {
          await configProfileStore.reorderGroupProfiles(props.groupId, nextIds);
        } else if (props.userId) {
          await configProfileStore.reorderUserProfiles(props.userId, nextIds);
        } else if (props.userGroupId) {
          await configProfileStore.reorderUserGroupProfiles(
            props.userGroupId,
            nextIds
          );
        }
      }

      try {
        if (affectedClientIds.length > 0) {
          patchAssignedProfileFlag(affectedClientIds, nextIds.length > 0);
          await refreshAffectedClients(affectedClientIds);
        }
      } catch (refreshError) {
        console.warn(
          'Failed to refresh clients after profile assignment:',
          refreshError
        );
      }

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('configProfile.assignSuccess'),
        life: 3000,
      });

      emit('profile-assigned');
    } catch (error) {
      console.error('Error assigning profiles:', error);

      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('configProfile.assignError'),
        life: 4000,
      });
    } finally {
      if (affectedClientIds.length > 0) {
        clientsStore.setProfileAssignmentPending(affectedClientIds, false);
      }

      isSaving.value = false;
    }
  };

  void runAssignment();

  closeDialog();
}

// remove from the *current selection* only
function removeProfile(profileId: number) {
  selectedProfiles.value = selectedProfiles.value.filter((id) => id !== profileId);
}

function closeDialog() {
  isDialogOpen.value = false;
  emit('update:isOpen', false);
  activeLoadRequests.value = 0;
  selectedProfiles.value = [];
  initialAssignedIds.value = [];
  assignedProfiles.value = [];
  profiles.value = [];
}
function submitForm() {
  handleProfileAssignment();
}

function onDragStart(event: DragEvent, profile: ConfigProfiles) {
  if (event.dataTransfer) {
    draggedProfile.value = profile;
    event.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        id: profile.id,
        profileName: profile.profileName,
      })
    );

    if (event.target instanceof HTMLElement) {
      event.target.classList.add('dragging');
    }
  }
}

function onDragEnd(event: DragEvent) {
  draggedProfile.value = null;

  // Remove visual class from all elements
  document.querySelectorAll('.dragging').forEach(el => {
    el.classList.remove('dragging');
  });

  document.querySelectorAll('.active').forEach(el => {
    el.classList.remove('active');
  });
}

function onDrop(event: DragEvent) {
  event.preventDefault();
  if (draggedProfile.value && draggedProfile.value.id) {
    const profileId = draggedProfile.value.id;
    if (!selectedProfiles.value.includes(profileId)) {
      selectedProfiles.value.push(profileId);
    }
  }
}

onMounted(() => {
  if (props.isOpen) {
    loadAssignedProfiles().then(loadProfiles);
  }
});
</script>

<template>
  <div v-if="isDialogOpen" class="popup-overlay" @click.self="closeDialog">
    <div class="popup-container">
      <div class="popup-header">
        <h3>{{ t('configProfile.assign') }}</h3>
        <button class="close-button" @click="closeDialog">&times;</button>
      </div>

      <div class="popup-content">
        <div class="column left-column">
          <h4>{{ t('configProfile.available') }}</h4>
          <div class="profiles-list">
            <div v-if="isLoadingProfiles" class="loading-profiles">
              <i class="pi pi-spin pi-spinner"></i> {{ t('common.actions.loading') }}
            </div>
            <div v-else-if="profiles.length === 0" class="no-profiles">
              {{ t('configProfile.noProfiles') }}
            </div>
            <div
              v-else
              v-for="profile in profiles"
              :key="profile.id"
              :class="['profile-item', { selected: selectedProfiles.includes(profile.id) }]"
              draggable="true"
              @dragstart="onDragStart($event, profile)"
              @dragend="onDragEnd($event)"
            >
              <div class="profile-drag-handle"></div>
              <div class="profile-info">
                <div class="profile-name">{{ profile.profileName }}</div>
                <div class="profile-description">{{ profile.description }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- right column -->
        <div class="column right-column"
             @dragover.prevent
             @drop="onDrop"
        >
          <h4>{{ t('configProfile.assigned') }}</h4>

          <div
            v-if="combinedProfiles.length === 0"
            class="no-selected-profiles"
            :class="{ 'drop-active': isDropActive }"
            @dragover.prevent="isDropActive = true"
            @dragleave="isDropActive = false"
            @drop="onDrop($event); isDropActive = false"
          >
            {{ t('configProfile.noAssigned') }}
          </div>

          <div v-else>
            <!-- In the right column, always show the remove button -->
            <div
              v-for="(profile, idx) in combinedProfiles"
              :key="profile.id"
              class="assigned-profile-item"
              draggable="true"
              @dragstart="onCombinedDragStart($event, idx)"
              @dragover.prevent="onCombinedDragOver($event, idx)"
            >
              <span>{{ profile.profileName }}</span>
              <button
                class="remove-profile-button"
                @click="removeProfile(profile.id)"
                :disabled="loading"
              >
                <i class="pi pi-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="popup-footer">
        <Button
          :label="t('configProfile.save')"
          class="button submit"
          @click="submitForm"
          :disabled="!hasChanges || loading"
        />
        <Button
          :label="t('configProfile.cancel')"
          class="button cancel"
          @click="closeDialog"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-container {
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 4px;
  width: 800px;
  max-width: 90%;
  height: 40rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  height: 3rem;
  border-bottom: 1px solid hsl(var(--border));
  background-color: hsl(var(--secondary-nav-bg));
}

.popup-header h2 {
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
}

.popup-content {
  display: flex;
  padding: 15px;
  overflow-y: auto;
  align-items: stretch;
  min-height: 33rem;
  background-color: hsl(var(--secondary-nav-bg));
}

.column {
  flex: 1;
  padding: 0 10px;
  display: flex;
  flex-direction: column;
}

.left-column {
  border-right: 1px solid hsl(var(--border));
}

h3,
h4 {
  margin-top: 0;
  margin-bottom: 10px;
}

.profiles-list {
  overflow-y: auto;
}

.loading-profiles,
.no-profiles {
  padding: 20px;
  text-align: center;
  color: #666;
}

.profile-item {
  display: flex;
  align-items: center;
  padding: 16px;
  margin: 10px 8px;
  height: 2.65rem;
  background: hsl(var(--background));
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(33, 150, 243, 0.08), 0 1.5px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid hsl(var(--border));
  cursor: move;
  transition: box-shadow 0.2s, background 0.2s;
}

.profile-item.selected {
  background-color: hsl(var(--sidebar-accent));
  border-left: 4px solid hsl(var(--primary));
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.16);
}

.profile-item:hover {
  background-color: hsl(var(--secondary-nav-hover));
  box-shadow: 0 4px 6px hsl(var(--primary) / 0.3);
}

.profile-item.dragging {
  opacity: 0.5;
  background-color: #e3f2fd;
}

.profile-drag-handle {
  margin-right: 10px;
  color: #999;
}

.profile-info {
  flex: 1;
}

.profile-name {
  font-weight: 300;
}

.profile-description {
  font-size: 0.85rem;
  color: #666;
}

.no-selected-profiles {
  padding: 20px;
  text-align: center;
  color: #666;
  margin-top: 20px;
  border: 2px dashed;
  border-radius: 8px;
  background-color: hsl(var(--secondary-nav-bg));
}

.no-selected-profiles.drop-active {
  background-color: hsl(var(--secondary-nav-hover));
  border-color: hsl(var(--primary));
  color: hsl(var(--primary));
  transition: background 0.2s, border 0.2s, color 0.2s;
}

.selected-profile-item,
.assigned-profile-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.selected-profile-item {
  background-color: #e3f2fd;
  margin: 5px;
  border-radius: 4px;
}

.assigned-profile-item {
  display: flex;
  align-items: center;
  padding: 16px;
  margin: 10px 8px;
  border-radius: 4px;
  height: 2.65rem;
  box-shadow: 0 2px 6px rgba(33, 150, 243, 0.08), 0 1.5px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid hsl(var(--border));
  transition: box-shadow 0.2s, background 0.2s;
}

.assigned-profile-item:hover {
  background-color: hsl(var(--secondary-nav-hover));
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.16);
}

.remove-profile-button {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-profile-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #d32f2f;
}

.empty-drop-area i {
  font-size: 2rem;
  margin-bottom: 10px;
}

.popup-footer {
  display: flex;
  justify-content: flex-end;
  padding: 15px;
  border-top: 1px solid hsl(var(--border));
  background-color: hsl(var(--secondary-nav-bg));
  gap: 10px;
}

.button {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  margin-left: 10px;
}

.cancel {
  background-color: #fbc02d;
}

.submit {
  background-color: #689f38;
  color: #fff;
}
</style>
